import { PAYMENT_TARIFF } from '../../../types';
import { getParameterFromURL } from '../../../utils/scripts';
import CalculateOfferPrices from '../../../utils/scripts/calculateOfferPrices';
import { calculatePrice } from '../../../utils/scripts/calculatePrice';
import type { WebCatalogProduct } from '../../../utils/services/EveryDoggyAPITypes';
import { getPricesConfig } from '../../getPricesConfig';
import { DEFAULT_DISCOUNT } from '../../discountConfig';

type EverydoggyNewDesignConfig = NonNullable<ReturnType<typeof getPricesConfig>['everydoggyNewDesign']>;

export type OfferPriceConfig = EverydoggyNewDesignConfig['firstPriceConfig'];

export type OfferProducts = {
  firstOption?: WebCatalogProduct;
  secondOption?: WebCatalogProduct;
  thirdOption?: WebCatalogProduct;
};

export const OPTION_DISCOUNT_PERCENT = DEFAULT_DISCOUNT;

export const EMPTY_OFFER_PRODUCTS: OfferProducts = {
  firstOption: undefined,
  secondOption: undefined,
  thirdOption: undefined,
};

const SPECIAL_OFFER_TARIFFS: PAYMENT_TARIFF[] = [
  PAYMENT_TARIFF.V22,
  PAYMENT_TARIFF.V28,
  PAYMENT_TARIFF.V30,
  PAYMENT_TARIFF.V32,
  PAYMENT_TARIFF.V36,
  PAYMENT_TARIFF.V38,
  PAYMENT_TARIFF.V40,
  PAYMENT_TARIFF.V42,
];

const MIX_DISCOUNT_OLD_PERIOD_PRICE_MAP: Partial<Record<PAYMENT_TARIFF, readonly [string, string, string]>> = {
  [PAYMENT_TARIFF.V31]: ['13.49', '7.54', '5.46'],
  [PAYMENT_TARIFF.V32]: ['13.49', '7.54', '5.46'],
  [PAYMENT_TARIFF.V37]: ['5.38', '4.41', '3.14'],
  [PAYMENT_TARIFF.V38]: ['5.38', '4.41', '3.14'],
};

const DEFAULT_OLD_PERIOD_PRICES = ['10.62', '2.79', '2.02'] as const;
const RED_LABEL_OLD_PERIOD_PRICES = ['4.97', '4.07', '2.89'] as const;

export { getCurrencySymbol } from '../../../utils/scripts/currency';

export function isSpecialOfferTariff(tariff: PAYMENT_TARIFF) {
  return SPECIAL_OFFER_TARIFFS.includes(tariff);
}

export function shouldShowRedLabelDiscount(tariff: PAYMENT_TARIFF) {
  return getParameterFromURL('p20v1RedLabelDiscountNoSpecial') === 'true' && tariff === PAYMENT_TARIFF.V41;
}

export function hasMixPaywallDiscountPercent() {
  return Boolean(getParameterFromURL('p13MixPaywallDiscountPercent'));
}

export function getLabelDiscountText(tariff: PAYMENT_TARIFF) {
  return isSpecialOfferTariff(tariff) ? '77' : `${DEFAULT_DISCOUNT}`;
}

export function getOptionDiscountLabel(promoCodeSave: string, discountPercent?: string | number) {
  const percent = Number(discountPercent) || OPTION_DISCOUNT_PERCENT;
  return `${percent}${promoCodeSave}`;
}

export function formatOfferTitle(title = '') {
  return title
    .toLowerCase()
    .replace(/-\w/g, match => match.toUpperCase())
    .replace(/^\w/, match => match.toUpperCase());
}

export function getPriceParts(price?: string | number | null) {
  if (price === undefined || price === null || price === '') {
    return { whole: '', cents: '' };
  }

  const normalizedPrice = Number(price);
  const whole = Number.isFinite(normalizedPrice) ? String(Math.trunc(normalizedPrice)) : '';
  const decimalPart = String(price).split('.')[1];

  return {
    whole,
    cents: decimalPart ? decimalPart.padEnd(2, '0') : '00',
  };
}

export function getFirstWeeklyPrice(product: WebCatalogProduct | undefined, config: OfferPriceConfig) {
  const isWeekTrial = product?.duration?.trial_period_unit === 'week' || product?.duration?.trial_period_value === 7;

  if (isWeekTrial) {
    return CalculateOfferPrices.setPriceFullV2({
      product,
      defaultPrice: config.defaultPricesPerWeek,
      calculateFromIntro: true,
    });
  }

  return CalculateOfferPrices.setPriceInWeeksIntro({
    product,
    defaultPricesPerWeek: config.defaultPricesPerWeek,
    calculateFromIntro: true,
  });
}

export function getRecurringWeeklyPrice(product: WebCatalogProduct | undefined, config: OfferPriceConfig) {
  return calculatePrice(product, {
    target: 'perWeek',
    source: 'auto',
    calculateFromIntro: true,
    defaultPrice: config.defaultPricesPerWeek,
  });
}

export function getDailyPrice(product: WebCatalogProduct | undefined, config: OfferPriceConfig) {
  return CalculateOfferPrices.setPriceOneDaytrial({
    product,
    defaultPrice: config.defaultPricePerDay,
    calculateFromIntro: true,
  });
}

export function normalizeDisplayedDayPrice(price: string | number, shouldFix = false) {
  if (!shouldFix) {
    return price;
  }

  return Number(price) === 0.28 ? '0.29' : price;
}

export function getOldPeriodPrices({
  tariff,
  firstPriceConfig,
  secondPriceConfig,
  thirdPriceConfig,
  mixDiscountPercentEnabled,
  redLabelDiscountEnabled,
}: {
  tariff: PAYMENT_TARIFF;
  firstPriceConfig: OfferPriceConfig;
  secondPriceConfig: OfferPriceConfig;
  thirdPriceConfig: OfferPriceConfig;
  mixDiscountPercentEnabled: boolean;
  redLabelDiscountEnabled: boolean;
}) {
  if (redLabelDiscountEnabled) {
    return {
      first: RED_LABEL_OLD_PERIOD_PRICES[0],
      second: RED_LABEL_OLD_PERIOD_PRICES[1],
      third: RED_LABEL_OLD_PERIOD_PRICES[2],
    };
  }

  const mixDiscountPrices = mixDiscountPercentEnabled ? MIX_DISCOUNT_OLD_PERIOD_PRICE_MAP[tariff] : undefined;

  return {
    first: mixDiscountPrices?.[0] || firstPriceConfig.defaultOldPricesForPeriod || DEFAULT_OLD_PERIOD_PRICES[0],
    second: mixDiscountPrices?.[1] || secondPriceConfig.defaultOldPricesForPeriod || DEFAULT_OLD_PERIOD_PRICES[1],
    third: mixDiscountPrices?.[2] || thirdPriceConfig.defaultOldPricesForPeriod || DEFAULT_OLD_PERIOD_PRICES[2],
  };
}
